import { Component, type ErrorInfo, type ReactNode } from 'react';
import { ErrorPage } from '@/components/error-page';

interface Props {
  children: ReactNode;
  fallback?: (err: Error, reset: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Top-level boundary so a render-time exception doesn't blank the SPA.
 * Network/data errors are caught by TanStack Query (and surfaced via toasts);
 * this is the safety net for genuinely unexpected render bugs.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info);
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    if (this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.reset);
      }
      // `routerless` is load-bearing: this boundary wraps RouterProvider, so
      // when it trips there is no router context — a TanStack <Link> here
      // would throw inside the fallback and blank the whole app.
      return (
        <ErrorPage
          variant="error"
          error={this.state.error}
          onRetry={this.reset}
          routerless
        />
      );
    }
    return this.props.children;
  }
}
